import { Any } from './Any'
import { Type } from '../utils/type'
import { Union, caseOf } from '../utils/union-type'
import { dispatch } from '../utils/dispatch'
import { foldl, map } from '../dispatchers'
import { maybe } from './Maybe'

export const Response = Type('Response', {
  url: String,
  status: Object,
  headers: Object,
  body: Any,
})

export const Error = Union('Error', {
  NetworkError: [Any],
  BadStatus: [Response],
  BadPayload: [Response],
})

export const Request = Union('Request', {
  Inactive: [],
  Pending: [],
  Done: [Any],
  Error: [Error],
})

dispatch.addMethod('fold', Request, (self, Inactive, Pending, Done, Error) => caseOf(self, { Inactive, Pending, Done, Error }))

dispatch.addMethod('map', Request, (self, f) => caseOf(self, { Done: (val) => Request.Done(f(val)), _: () => self }))

dispatch.addMethod('get', Request, (self, fallback) => {
  const val = caseOf(self, { Done: (vals) => vals, _: () => fallback })
  return fallback == null ? maybe(val) : val
})

/* get(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Returns a promise that will fetch data from a JSON endpoint.
 *
 * Example:
 *
 *   Http.get("/existing-endpoint", {}).then(data =>
 *     console.log(data)
 *   ) //=> Response({ ... })
 *
 *   Http.get("/nonexisting-endpoint", {}).catch(err =>
 *     console.log(err)
 *   ) //=> Http.NetworkError(Response( ... ))
 *
 */
export const get = (url, params = {}) => request(url, 'json', params)

/* getText(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Returns a promise that will fetch text from an API endpoint.
 *
 * Example:
 *
 *   Http.get("/existing-endpoint", {}).then(data =>
 *     console.log(data)
 *   ) //=> "foo bar"
 *
 *   Http.get("/nonexisting-endpoint", {}).catch(err =>
 *     console.log(err)
 *   ) //=> Http.NetworkError(Response)
 *
 */
export const getText = (url, params = {}) => request(url, 'text', params)

/* getBlob(url: String, params: Optional(Object)) // => HTTP.Request(Object, Error)
 *
 * Fetches a binary resource, like an image, and converts it into an object url
 *
 * Example:
 *
 *   Http.getBlob("/image.png", {}).then(data =>
 *     console.log(data)
 *   ) //=> "blob:/42f652bf-770e-4058-8b4b-c67a2a501c90"
 */
export const getBlob = (url, params = {}) => request(url, 'blob', params).then((blob) => URL.createObjectURL(blob))

/* post(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Posts json to an api endpoint
 *
 * Example:
 *
 *   Http.post("/posts", { body: { title: "My post", body: "This is a good post" } })
 *
 */
export const post = (url, params = {}) => {
  params = {
    ...params,
    headers:
      typeof params.body === 'object' && !(params.body instanceof FormData)
        ? {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(params.headers || {}),
          }
        : params.headers || {},
    method: 'post',
    body: typeof params.body === 'object' && !(params.body instanceof FormData) ? JSON.stringify(params.body) : params.body,
  }

  return request(url, 'json', params)
}

/* put(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Puts json to an api endpoint
 *
 * Example:
 *
 *   Http.put("/posts/1", { body: { id: 123, title: "My post", body: "This is a good post" } })
 *
 */
export const put = (url, params = {}) => {
  params = {
    ...params,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(params.headers || {}),
    },
    method: 'put',
    body: JSON.stringify(params.body),
  }

  return request(url, 'json', params)
}

/* patch(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Patches the resource at an api endpoint
 *
 * Example:
 *
 *   Http.patch("/posts/1", { body: { title: "My post" } })
 *
 */
export const patch = (url, params = {}) => {
  params = {
    ...params,
    method: 'patch',
    body: typeof params.body === 'object' ? JSON.stringify(params.body) : params.body,
  }

  return request(url, 'json', params)
}

/* destroy(url: String, params: Optional(Object)) => HTTP.Request(Object, Error)
 *
 * Deletes the resource at an api endpoint
 *
 * Example:
 *
 *   Http.destroy("/posts/1")
 *
 */
export const destroy = (url, params = {}) => request(url, 'json', { ...params, method: 'delete' })

// -- UTILS

/* normalize(request: Request, { key: String = 'id', transform: Function = identity })
 *
 * Given a request wrapping a list it will transform it to an object
 * containing a list of ids and a mapping from id to value optionally transformed
 * by a provided transform function.
 *
 * Example:
 *
 *   normalize(Request.Done([{ id: 1, name: "foo" }, { id: 2, name: "bar" }]))
 *     // => Request.Done({ ids: [1, 2], byId: { 1: { id: 1, name: "foo" }, 2: { id: 2, name: "bar" } } })
 *
 *   normalize(Request.Done([{ uuid: 1, name: "foo" }, { uuid: 2, name: "bar" }]), { key: 'uuid' })
 *     // => Request.Done({ ids: [1, 2], byId: { 1: { uuid: 1, name: "foo" }, 2: { uuid: 2, name: "bar" } } })
 *
 *   const User = Type('User', { id: Number, name: String })
 *   normalize(Request.Done([{ id: 1, name: "foo" }, { id: 2, name: "bar" }]), { transform: User })
 *     // => Request.Done({ ids: [1, 2], byId: { 1: User(1, 'foo'), 2: User(2, 'bar') } })
 */
export const normalize = (response, opts = {}) =>
  map(response, (vals) =>
    foldl(
      vals,
      ({ ids, byId }, val) => ({
        ids: ids.concat(val[opts.key || 'id']),
        byId: {
          ...byId,
          [val[opts.key || 'id']]: opts.transform ? opts.transform(val) : val,
        },
      }),
      { ids: [], byId: {} },
    ),
  )

export const reject = (response) => Promise.reject(Request.Error(Error.BadPayload(createResponse(response))))

const createResponse = (resp, body = {}) => {
  let headers = {}

  if (typeof resp.headers.keys === 'function') {
    for (const key of resp.headers.keys()) headers[key] = resp.headers.get(key)
  } else {
    headers = resp.headers
  }

  return Response(resp.url, { code: resp.status, message: resp.statusText }, headers, body)
}

const request = (url, contentType, params) => {
  if (params.params) {
    url =
      url +
      '?' +
      Object.keys(params.params)
        .map((key) => `${key}=${params.params[key]}`)
        .join('&')
  }

  return fetch(url, params)
    .catch((ex) => Promise.reject(Error.NetworkError(ex)))
    .then((resp) => {
      if (!resp.ok) return Promise.reject(Request.Error(Error.BadStatus(createResponse(resp))))

      return resp[contentType]()
        .then((json) => Promise.resolve(createResponse(resp, json)))
        .catch(() => Promise.reject(Request.Error(Error.BadPayload(createResponse(resp)))))
    })
}

export const HTTP = {
  Response,
  Error,
  Request,
  get,
  getText,
  getBlob,
  post,
  put,
  patch,
  destroy,
  normalize,
  reject,
  createResponse,
}
